import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  AuthService,
  CustomerService,
  ResourceService,
  formatCustomerDisplayName,
  parseDateFromBackend,
} from "../../services";
import { CustomerAvatar, MultiSelectDropdown } from "../../shared";
import {
  BULK_EDIT_TAB_LABELS,
  findBulkEditField,
  getBulkEditFieldSections,
  persistKeysForBulkEditField,
} from "./bulkEditCustomerFields";

const customerIdOf = (customer) => {
  const raw = customer?.id ?? customer?._id ?? "";
  if (raw && typeof raw === "object") {
    if (typeof raw.toHexString === "function") return raw.toHexString();
    return `${raw._id || raw.$oid || raw.id || ""}`.trim();
  }
  return `${raw}`.trim();
};

const CUSTOMER_UPDATE_OMIT_KEYS = new Set(["_id", "id", "__v", "password", "createdAt", "updatedAt"]);

const customerRecordForUpdate = (record) => {
  const next = {};
  Object.keys(record || {}).forEach((key) => {
    if (CUSTOMER_UPDATE_OMIT_KEYS.has(key)) return;
    next[key] = record[key];
  });
  return next;
};

const editorDisplayName = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.name || user?.username || "";
  } catch (_err) {
    return "";
  }
};

const formatDateForBackend = (date) => {
  if (!date) return "";
  const d = date instanceof Date ? date : parseDateFromBackend(date);
  if (!d || Number.isNaN(d.getTime())) return "";
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
};

const emptyValueForField = (field) => {
  if (!field) return "";
  if (field.input === "multiselect") return [];
  if (field.input === "date") return null;
  return "";
};

const needsOtherValue = (field, value) => {
  if (!field?.otherKey) return false;
  if (field.input === "multiselect") {
    return (value || []).some((item) => `${item?.value || ""}` === "other");
  }
  return `${value || ""}` === "other";
};

const optionLabel = (field, value) => {
  const raw = `${value || ""}`;
  if (!raw) return "";
  const grouped = Array.isArray(field?.groupedOptions) ? field.groupedOptions : null;
  const options = grouped
    ? grouped.flatMap((group) => group.options || [])
    : field?.options || [];
  const match = options.find((option) => `${option.value}` === raw);
  return match?.label || raw;
};

const normalizeResourceText = (value) => `${value || ""}`.toLowerCase().replace(/\s+/g, " ").trim();

const isPcpSpecialty = (provider) => {
  const specialty = normalizeResourceText(provider?.specialty || provider?.data?.specialty);
  return specialty === "family medicine (pcp)" || specialty === "family medicine pcp";
};

const resourceRecordId = (record) => `${record?.id ?? record?._id ?? ""}`.trim();

const mapResourceSelectOptions = (records = []) =>
  [...records]
    .map((record) => ({
      value: resourceRecordId(record) || `${record?.name || ""}`.trim(),
      label: `${record?.name || ""}`.trim(),
    }))
    .filter((option) => option.label)
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

const withOrphanSelectOption = (options = [], value) => {
  const raw = `${value || ""}`.trim();
  if (!raw) return options;
  if (options.some((option) => `${option.value}` === raw)) return options;
  return [{ value: raw, label: raw }, ...options];
};

const WizardStepper = ({ step }) => {
  const current = step === "fields" ? 1 : step === "mode" ? 2 : 3;
  return (
    <div className="bulk-edit-stepper" aria-hidden="true">
      <span className={`bulk-edit-step-dot${current >= 1 ? (current === 1 ? " current" : " done") : ""}`}>1</span>
      <span className={`bulk-edit-step-line${current > 1 ? " active" : ""}`} />
      <span className={`bulk-edit-step-dot${current >= 2 ? (current === 2 ? " current" : " done") : ""}`}>2</span>
      <span className={`bulk-edit-step-line${current > 2 ? " active" : ""}`} />
      <span className={`bulk-edit-step-dot${current >= 3 ? " current" : ""}`}>3</span>
    </div>
  );
};

const BulkEditCustomersWizard = ({ customers = [], onCancel, onComplete }) => {
  const fieldSections = useMemo(
    () => getBulkEditFieldSections((tab) => AuthService.canEditCustomerTab(tab)),
    []
  );
  const [step, setStep] = useState("fields");
  const [fieldKey, setFieldKey] = useState("");
  const [mode, setMode] = useState("");
  const [sameValue, setSameValue] = useState("");
  const [sameOtherValue, setSameOtherValue] = useState("");
  const [manualValues, setManualValues] = useState({});
  const [applying, setApplying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmError, setConfirmError] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0, name: "", field: "" });
  const [successCount, setSuccessCount] = useState(0);
  const [successSummary, setSuccessSummary] = useState("");
  const [pcpOptions, setPcpOptions] = useState([]);
  const [pharmacyOptions, setPharmacyOptions] = useState([]);

  const selectedField = useMemo(() => {
    const field = findBulkEditField(fieldKey, fieldSections);
    if (!field) return null;
    if (field.resource === "pcp") return { ...field, options: pcpOptions };
    if (field.resource === "pharmacy") return { ...field, options: pharmacyOptions };
    return field;
  }, [fieldKey, fieldSections, pcpOptions, pharmacyOptions]);
  const selectedCount = customers.length;

  useEffect(() => {
    let cancelled = false;
    const loadPcp = fieldKey === "primary_care_physician";
    const loadPharmacy = fieldKey === "pharmacy";
    if (!loadPcp && !loadPharmacy) return undefined;
    if (loadPcp) {
      ResourceService.getAll()
        .then((response) => {
          if (cancelled) return;
          const providers = (response?.data || []).filter((provider) => {
            const status = normalizeResourceText(provider?.status || "active");
            return status === "active" && isPcpSpecialty(provider);
          });
          setPcpOptions(mapResourceSelectOptions(providers));
        })
        .catch(() => {
          if (!cancelled) setPcpOptions([]);
        });
    }
    if (loadPharmacy) {
      ResourceService.getAll("pharmacy")
        .then((response) => {
          if (cancelled) return;
          setPharmacyOptions(
            mapResourceSelectOptions(
              (response?.data || []).filter((record) => normalizeResourceText(record?.status || "active") === "active")
            )
          );
        })
        .catch(() => {
          if (!cancelled) setPharmacyOptions([]);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [fieldKey]);

  const getCurrentRawValue = (customer, field) => {
    if (!field) return "";
    const raw = customer?.[field.key];
    if (field.input === "multiselect") {
      const values = Array.isArray(raw) ? raw : raw ? [raw] : [];
      return values.map((item) => {
        const value = typeof item === "object" ? item.value : item;
        return { value, label: optionLabel(field, value) };
      });
    }
    if (field.input === "date") return parseDateFromBackend(raw) || null;
    if (field.idKey) {
      const id = `${customer?.[field.idKey] ?? ""}`.trim();
      if (id) return id;
      const name = `${raw ?? ""}`.trim();
      const match = (field.options || []).find((option) => `${option.label}` === name);
      return match?.value || name;
    }
    if (field.key === "primary_care_physician") {
      return `${raw || customer?.care_provider || ""}`.trim();
    }
    return raw ?? "";
  };

  const formatDisplayValue = (customer, field) => {
    if (!field) return "Empty";
    const raw = customer?.[field.key];
    if (field.input === "multiselect") {
      const values = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const labels = values
        .map((item) => optionLabel(field, typeof item === "object" ? item.value : item))
        .filter(Boolean);
      return labels.length ? labels.join(", ") : "Empty";
    }
    if (field.input === "date") {
      return `${raw || ""}`.trim() || "Empty";
    }
    const label = optionLabel(field, raw);
    return `${label || ""}`.trim() || "Empty";
  };

  const buildFieldsPayload = (value, otherValue, customer) => {
    if (!selectedField) return null;
    const fields = {};
    if (selectedField.input === "date") {
      fields[selectedField.key] = formatDateForBackend(value);
    } else if (selectedField.input === "multiselect") {
      fields[selectedField.key] = (value || [])
        .map((item) => (item && typeof item === "object" ? item.value : item))
        .map((item) => `${item ?? ""}`.trim())
        .filter(Boolean);
    } else {
      fields[selectedField.key] = `${value ?? ""}`;
    }
    if (selectedField.key === "legal_sex") {
      fields.gender = selectedField.input === "date" ? "" : (value || "");
    }
    if (selectedField.key === "primary_care_physician") {
      const match = (selectedField.options || []).find((option) => `${option.value}` === `${value ?? ""}`);
      const name = match?.label || `${value ?? ""}`.trim();
      fields.primary_care_physician = name;
      fields.care_provider = name;
    }
    if (selectedField.idKey) {
      const match = (selectedField.options || []).find((option) => `${option.value}` === `${value ?? ""}`);
      fields[selectedField.key] = match?.label || `${value ?? ""}`.trim();
      fields[selectedField.idKey] = match?.value || "";
    }
    if (needsOtherValue(selectedField, value)) {
      fields[selectedField.otherKey] = otherValue || "";
    } else if (selectedField.otherKey) {
      fields[selectedField.otherKey] = "";
    }
    persistKeysForBulkEditField(selectedField).forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(fields, key)) {
        fields[key] = selectedField.input === "multiselect" && key === selectedField.key ? [] : "";
      }
    });
    if (selectedField.key === "firstname" || selectedField.key === "lastname") {
      const first = selectedField.key === "firstname" ? `${value ?? ""}` : `${customer?.firstname || ""}`;
      const last = selectedField.key === "lastname" ? `${value ?? ""}` : `${customer?.lastname || ""}`;
      fields.name = `${last},${first}`;
    }
    if (selectedField.key === "language_spoken") {
      const labels = (value || [])
        .map((item) => item?.label || optionLabel(selectedField, typeof item === "object" ? item.value : item))
        .filter(Boolean);
      fields.language = labels.join(", ");
    }
    if (selectedField.key === "consent_to_text_messages") {
      fields.text_msg_enabled = `${value || ""}` === "yes";
    }
    if (selectedField.key === "eyes_on") {
      fields.disability = `${value || ""}` === "yes";
    }
    if (
      selectedField.key === "emergency_contact_name" ||
      selectedField.key === "emergency_contact_phone" ||
      selectedField.key === "emergency_contact_relationship"
    ) {
      const name =
        selectedField.key === "emergency_contact_name"
          ? fields.emergency_contact_name
          : `${customer?.emergency_contact_name || ""}`;
      const phone =
        selectedField.key === "emergency_contact_phone"
          ? fields.emergency_contact_phone
          : `${customer?.emergency_contact_phone || ""}`;
      const relationship =
        selectedField.key === "emergency_contact_relationship"
          ? fields.emergency_contact_relationship
          : `${customer?.emergency_contact_relationship || ""}`;
      fields.emergency_contact = `${name} ${phone} ${relationship}`.trim();
    }
    if (selectedField.syncAdmissionRecord) {
      const records =
        Array.isArray(customer?.admission_records) && customer.admission_records.length
          ? customer.admission_records.map((row) => ({ ...row }))
          : [{}];
      records[0] = {
        ...records[0],
        [selectedField.key]: fields[selectedField.key],
      };
      if (selectedField.otherKey) {
        records[0][selectedField.otherKey] = fields[selectedField.otherKey] || "";
      }
      fields.admission_records = records;
    }
    return fields;
  };

  const renderValueControl = ({ value, onChange, otherValue, onOtherChange, idPrefix }) => {
    if (!selectedField) return null;
    if (selectedField.input === "select") {
      const selectOptions = withOrphanSelectOption(selectedField.options || [], value);
      return (
        <>
          <Form.Select
            id={idPrefix}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Choose value</option>
            {Array.isArray(selectedField.groupedOptions)
              ? selectedField.groupedOptions.map((group, groupIdx) => (
                  <optgroup key={`${group.label || "group"}-${groupIdx}`} label={group.label || " "}>
                    {(group.options || []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ))
              : selectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
          </Form.Select>
          {needsOtherValue(selectedField, value) && (
            <Form.Control
              className="mt-2"
              type="text"
              placeholder="Please specify"
              value={otherValue || ""}
              onChange={(e) => onOtherChange(e.target.value)}
            />
          )}
        </>
      );
    }
    if (selectedField.input === "multiselect") {
      return (
        <>
          <MultiSelectDropdown
            value={value || []}
            onChange={onChange}
            options={selectedField.groupedOptions || selectedField.options || []}
            placeholder="Select..."
            exclusiveOptionValue={selectedField.exclusiveOptionValue || null}
          />
          {needsOtherValue(selectedField, value) && (
            <Form.Control
              className="mt-2"
              type="text"
              placeholder="Please specify"
              value={otherValue || ""}
              onChange={(e) => onOtherChange(e.target.value)}
            />
          )}
        </>
      );
    }
    if (selectedField.input === "date") {
      return (
        <DatePicker
          selected={value}
          onChange={(date) => onChange(date)}
          dateFormat="MM/dd/yyyy"
          placeholderText="MM/DD/YYYY"
          className="form-control"
          showYearDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={100}
          isClearable
        />
      );
    }
    if (selectedField.input === "textarea") {
      return (
        <Form.Control
          as="textarea"
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    return (
      <Form.Control
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={selectedField.placeholder || "Enter value"}
      />
    );
  };

  const goToMode = () => {
    if (!selectedField) return;
    setStep("mode");
  };

  const goToValues = () => {
    if (!mode) return;
    if (mode === "manual") {
      const next = {};
      customers.forEach((customer) => {
        const id = customerIdOf(customer);
        next[id] = {
          value: getCurrentRawValue(customer, selectedField),
          otherValue: customer?.[selectedField.otherKey] || "",
        };
      });
      setManualValues(next);
    }
    setStep("values");
  };

  const sameValuePreview = () => {
    if (!selectedField) return "New value will apply to everyone";
    if (selectedField.input === "multiselect") {
      const labels = (sameValue || []).map((item) => item.label || optionLabel(selectedField, item.value)).filter(Boolean);
      return labels.length ? labels.join(", ") : "New value will apply to everyone";
    }
    if (selectedField.input === "date") {
      return formatDateForBackend(sameValue) || "New value will apply to everyone";
    }
    const label = optionLabel(selectedField, sameValue);
    const extra = needsOtherValue(selectedField, sameValue) && sameOtherValue ? ` (${sameOtherValue})` : "";
    return `${label || ""}`.trim() ? `${label}${extra}` : "New value will apply to everyone";
  };

  const isSameValueReady = () => {
    if (!selectedField) return false;
    if (selectedField.input === "multiselect") return (sameValue || []).length > 0;
    if (selectedField.input === "date") return !!sameValue;
    return `${sameValue || ""}`.trim() !== "";
  };

  const openConfirm = () => {
    if (mode === "same" && !isSameValueReady()) {
      window.alert("Please enter or choose the value to apply to everyone.");
      return;
    }
    setConfirmText("");
    setConfirmError(false);
    setApplyError("");
    setShowConfirm(true);
  };

  const finishSuccess = (updated) => {
    setSuccessCount(updated);
    if (mode === "delete") setSuccessSummary("all values cleared");
    else if (mode === "manual") setSuccessSummary("customer-specific values updated");
    else setSuccessSummary(sameValuePreview());
    setShowConfirm(false);
    setStep("success");
  };

  const applyChanges = async () => {
    if (confirmText !== "CONFIRM") {
      setConfirmError(true);
      return;
    }
    if (!AuthService.canEditAnyCustomerTab() || (selectedField?.tab && !AuthService.canEditCustomerTab(selectedField.tab))) {
      setApplyError("You do not have permission to bulk edit customer info.");
      return;
    }
    const ids = customers.map(customerIdOf).filter(Boolean);
    if (!ids.length) {
      setApplyError("Select at least one customer.");
      return;
    }
    const jobs = ids.map((id) => {
      const listed = customers.find((customer) => customerIdOf(customer) === id);
      const entry =
        mode === "manual"
          ? manualValues[id] || { value: emptyValueForField(selectedField), otherValue: "" }
          : {
              value: mode === "delete" ? emptyValueForField(selectedField) : sameValue,
              otherValue: mode === "delete" ? "" : sameOtherValue,
            };
      return {
        id,
        value: entry.value,
        otherValue: entry.otherValue,
        fields: buildFieldsPayload(entry.value, entry.otherValue, listed) || {},
      };
    });
    if (!jobs.some((job) => Object.keys(job.fields || {}).length > 0)) {
      setApplyError("Select a field and value to apply.");
      return;
    }
    setShowConfirm(false);
    setApplying(true);
    setApplyError("");
    setSaveProgress({
      current: 0,
      total: jobs.length,
      name: "",
      field: selectedField?.label || "",
    });
    setStep("saving");
    try {
      let updated = 0;
      const failedMessages = [];
      const editBy = editorDisplayName();
      for (let index = 0; index < jobs.length; index += 1) {
        const job = jobs[index];
        const listed = customers.find((customer) => customerIdOf(customer) === job.id);
        setSaveProgress({
          current: index + 1,
          total: jobs.length,
          name: formatCustomerDisplayName(listed) || job.id,
          field: selectedField?.label || "",
        });
        try {
          const existingRes = await CustomerService.getCustomer(job.id);
          let record = customerRecordForUpdate(existingRes?.data || listed || {});
          const fields = buildFieldsPayload(job.value, job.otherValue, record) || job.fields || {};
          const fieldKeys = Object.keys(fields);
          if (fieldKeys.length) {
            for (const fieldName of fieldKeys) {
              record = {
                ...record,
                [fieldName]: fields[fieldName],
                edit_by: editBy,
              };
              await CustomerService.updateCustomer(job.id, record);
            }
          }
          updated += 1;
        } catch (err) {
          failedMessages.push(err?.response?.data?.message || err?.message || job.id);
        }
      }
      if (updated <= 0) {
        throw new Error(failedMessages[0] || "Failed to bulk edit customers.");
      }
      finishSuccess(updated);
    } catch (error) {
      setApplying(false);
      setStep("values");
      window.alert(error?.response?.data?.message || error?.message || "Failed to bulk edit customers.");
    } finally {
      setApplying(false);
    }
  };

  const heading = (
    <div className="bulk-edit-page-heading">
      <div>
        <button
          type="button"
          className="bulk-edit-back-link"
          onClick={() => {
            if (step === "fields") onCancel?.();
            else if (step === "mode") setStep("fields");
            else setStep("mode");
          }}
        >
          ← {step === "fields" ? "Back to Customers" : "Back"}
        </button>
        <h4>Bulk Edit</h4>
        <p className="bulk-edit-subtitle">{selectedCount} Customer{selectedCount === 1 ? "" : "s"} Selected</p>
      </div>
      <WizardStepper step={step} />
    </div>
  );

  if (step === "saving") {
    return (
      <div className="bulk-edit-wizard">
        <div className="bulk-edit-success-card bulk-edit-saving-card">
          <Spinner animation="border" role="status" className="bulk-edit-saving-spinner">
            <span className="visually-hidden">Saving</span>
          </Spinner>
          <h4>Applying changes</h4>
          <p className="bulk-edit-saving-copy">
            Updating customer {saveProgress.current || 1} of {saveProgress.total || customers.length}
            {saveProgress.name ? `: ${saveProgress.name}` : ""}
          </p>
          {saveProgress.field ? (
            <div className="bulk-edit-success-fields">
              <div>
                <strong>{saveProgress.field}</strong>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="bulk-edit-wizard">
        <div className="bulk-edit-success-card">
          <div className="bulk-edit-success-icon">✓</div>
          <h4>Bulk update complete</h4>
          <p>
            <strong>
              {successCount} customer{successCount === 1 ? "" : "s"}
            </strong>{" "}
            were successfully updated.
          </p>
          <div className="bulk-edit-success-fields">
            <div>
              <strong>{selectedField?.label}</strong>: {successSummary}
            </div>
          </div>
          <Button variant="primary" onClick={() => onComplete?.()}>
            Return to Customer List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-edit-wizard">
      {heading}
      <div className={`bulk-edit-card${step === "mode" ? " is-method" : ""}`}>
        {step === "fields" && (
          <>
            <div className="bulk-edit-card-header">
              <h5>Select fields to edit</h5>
              <p>Choose one field to update for all selected customers.</p>
            </div>
            <div className="bulk-edit-field-sections">
              {fieldSections.length === 0 ? (
                <div className="bulk-edit-empty-fields">
                  You do not have permission to edit any customer info fields.
                </div>
              ) : (
                fieldSections.map((section, index) => (
                <section key={`${section.tab}-${section.group}`} className="bulk-edit-field-section">
                  {index === 0 || fieldSections[index - 1].tab !== section.tab ? (
                    <div className="bulk-edit-tab-header">
                      {BULK_EDIT_TAB_LABELS[section.tab] || section.tab}
                    </div>
                  ) : null}
                  <div className="bulk-edit-field-section-header">
                    <h6>{section.group}</h6>
                  </div>
                  <div className="bulk-edit-field-grid">
                    {section.fields.map((field) => (
                      <label key={field.key} className="bulk-edit-field-option">
                        <input
                          type="radio"
                          name="bulk-edit-field"
                          checked={fieldKey === field.key}
                          onChange={() => {
                            setFieldKey(field.key);
                            setSameValue(emptyValueForField(field));
                            setSameOtherValue("");
                            setMode("");
                          }}
                        />
                        {field.label}
                      </label>
                    ))}
                  </div>
                </section>
                ))
              )}
            </div>
            <div className="bulk-edit-footer-actions">
              <Button variant="outline-secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" disabled={!fieldKey} onClick={goToMode}>
                Next
              </Button>
            </div>
          </>
        )}

        {step === "mode" && (
          <>
            <div className="bulk-edit-card-header">
              <h5>Choose how to update the field</h5>
              <p>Select one update method for all selected customers.</p>
            </div>
            <div className="bulk-edit-method-list">
              {[
                {
                  value: "same",
                  title: "Apply the same value to everyone",
                  copy: "Enter one new value and apply it to every selected customer.",
                },
                {
                  value: "delete",
                  title: "Clear all values",
                  copy: "Remove the current value from this field for every selected customer.",
                },
                {
                  value: "manual",
                  title: "Enter values manually",
                  copy: "Edit the selected field separately for each customer.",
                },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`bulk-edit-method-option${mode === option.value ? " is-selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="bulkValueMode"
                    value={option.value}
                    checked={mode === option.value}
                    onChange={() => {
                      setMode(option.value);
                      if (option.value !== "same") {
                        setSameValue(emptyValueForField(selectedField));
                        setSameOtherValue("");
                      }
                    }}
                  />
                  <span>
                    <strong>{option.title}</strong>
                    <small>{option.copy}</small>
                  </span>
                </label>
              ))}
            </div>
            <div className="bulk-edit-footer-actions">
              <Button variant="outline-secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" disabled={!mode} onClick={goToValues}>
                Next
              </Button>
            </div>
          </>
        )}

        {step === "values" && (
          <>
            <div className="bulk-edit-card-header">
              <h5>
                {mode === "same"
                  ? "Apply the same value"
                  : mode === "delete"
                    ? "Clear all values"
                    : "Enter values manually"}
              </h5>
              <p>
                {mode === "same"
                  ? "Enter one value to apply to every selected customer."
                  : mode === "delete"
                    ? "The selected field will be cleared for every selected customer."
                    : "Edit the selected field separately for each customer."}
              </p>
            </div>
            {mode === "same" && (
              <div className="bulk-edit-same-panel">
                <label className="bulk-edit-same-label">New value for all selected customers</label>
                {renderValueControl({
                  value: sameValue,
                  onChange: setSameValue,
                  otherValue: sameOtherValue,
                  onOtherChange: setSameOtherValue,
                  idPrefix: "sameBulkValueControl",
                })}
              </div>
            )}
            {mode === "delete" && (
              <div className="bulk-edit-clear-panel">
                This will clear the selected field for every selected customer.
              </div>
            )}
            {mode === "manual" && (
              <div className="bulk-edit-manual-panel">
                Enter or delete the value separately for each customer below.
              </div>
            )}
            <div className="bulk-edit-value-table-wrap">
              <table className="bulk-edit-value-table">
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>{selectedField?.label}</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => {
                    const id = customerIdOf(customer);
                    return (
                      <tr key={id}>
                        <td>
                          <div className="bulk-edit-matrix-customer">
                            <CustomerAvatar customerId={id} alt={formatCustomerDisplayName(customer)} size={44} />
                            <span>{formatCustomerDisplayName(customer)}</span>
                          </div>
                        </td>
                        <td>
                          {mode === "delete" ? (
                            <div className="bulk-edit-clear-preview">
                              <span className="old-value">{formatDisplayValue(customer, selectedField)}</span>
                              <span>→</span>
                              <span className="clear-label">Clear</span>
                            </div>
                          ) : mode === "same" ? (
                            <div className="bulk-edit-same-preview">{sameValuePreview()}</div>
                          ) : (
                            renderValueControl({
                              value: manualValues[id]?.value,
                              onChange: (next) =>
                                setManualValues((prev) => ({
                                  ...prev,
                                  [id]: { ...(prev[id] || {}), value: next },
                                })),
                              otherValue: manualValues[id]?.otherValue || "",
                              onOtherChange: (next) =>
                                setManualValues((prev) => ({
                                  ...prev,
                                  [id]: { ...(prev[id] || {}), otherValue: next },
                                })),
                              idPrefix: `manual-${id}`,
                            })
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="bulk-edit-footer-actions">
              <Button type="button" variant="outline-secondary" onClick={() => setStep("mode")}>
                Back
              </Button>
              <Button type="button" variant="outline-secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="button" variant="primary" onClick={openConfirm}>
                Apply Changes
              </Button>
            </div>
          </>
        )}
      </div>

      <Modal
        show={showConfirm}
        onHide={() => !applying && setShowConfirm(false)}
        centered
        dialogClassName="bulk-edit-confirm-modal"
      >
        <Modal.Header closeButton={!applying}>
          <Modal.Title>Apply bulk changes?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="bulk-edit-confirm-icon" aria-hidden="true">!</div>
          <p>
            You are about to update <strong>{selectedCount} customer{selectedCount === 1 ? "" : "s"}</strong>.
          </p>
          <div className="mb-3">
            <h6>Fields being changed</h6>
            <ul className="mb-0">
              <li>
                <strong>{selectedField?.label}</strong>
                {mode === "delete"
                  ? ": clear all values"
                  : mode === "manual"
                    ? ": customer-specific values entered manually"
                    : `: ${sameValuePreview()}`}
              </li>
            </ul>
          </div>
          <div className="bulk-edit-warning-box">
            These values will be applied to every selected customer.
          </div>
          <div className="bulk-edit-confirm-type">
            <label htmlFor="bulkEditConfirmText">
              Type <strong>CONFIRM</strong> to continue
            </label>
            <Form.Control
              id="bulkEditConfirmText"
              value={confirmText}
              autoComplete="off"
              placeholder="CONFIRM"
              onChange={(e) => {
                setConfirmText(e.target.value);
                setConfirmError(false);
              }}
            />
            {confirmError && <div className="text-danger small mt-1">Please type CONFIRM exactly.</div>}
            {applyError && <div className="text-danger small mt-2">{applyError}</div>}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={() => setShowConfirm(false)} disabled={applying}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={applyChanges}
            disabled={applying || confirmText !== "CONFIRM"}
          >
            {applying ? "Applying…" : "Apply Changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BulkEditCustomersWizard;
